import { useCallback } from 'react';
import { Button, Dropdown, TextInput } from '@carbon/react';
import { Add, TrashCan } from '@carbon/react/icons';
import { useAtomValue } from 'jotai';
import { formatNumber } from 'utils/number';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { enumsLoadableAtom } from './atoms';
import { useScenarioForm } from './contexts';
import type { EnumOption, RiskingEntry } from './types';
import { nextId } from './utils';
import styles from './risking-table.module.scss';

/** Maps title quality to default risk percentage. */
const TITLE_QUALITY_RISK_MAP: Record<string, number> = {
  none: 0,
  high: 50,
  medium: 75,
  low: 90,
};

interface RiskingTableProps {
  areaIndex: number;
}

/**
 * Editable risking entries for an area config.
 * Rows of inline form fields — labels shown on the first row only.
 * Supports General mode (Interest Type + Risk %) and Title mode
 * (Title Quality + Title Risk Factor + Interest Type + Risk %).
 * In Title mode, risk % auto-populates from the title quality mapping.
 * @param {RiskingTableProps} props - The component props
 * @return {JSX.Element} The risking entries accordion
 */
export function RiskingTable({ areaIndex }: RiskingTableProps): JSX.Element {
  const { form } = useScenarioForm();
  const prefix = `areas[${areaIndex}].riskingEntries` as const;

  const addEntry = useCallback(() => {
    const current = form.getFieldValue(prefix) as RiskingEntry[];
    form.setFieldValue(prefix, [
      ...current,
      {
        id: nextId(),
        riskType: 'title' as const,
        titleQuality: '',
        titleRiskFactor: '',
        interestType: '' as any,
        riskPercentage: '',
      },
    ]);
  }, [form, prefix]);

  const removeEntry = useCallback(
    (entryIndex: number) => {
      const current = form.getFieldValue(prefix) as RiskingEntry[];
      form.setFieldValue(
        prefix,
        current.filter((_, i) => i !== entryIndex)
      );
    },
    [form, prefix]
  );

  return (
    <Accordion defaultExpanded>
      <AccordionSummary>
        <h4 className={styles.title}>Risking Entries</h4>
      </AccordionSummary>
      <AccordionDetails>
        <div
          className={styles.tableWrapper}
          onClick={(e) => e.stopPropagation()}
        >
          <p className={styles.description}>
            Optionally apply title risking to this area. Each entry specifies a
            Title Quality level and Title Risk Factor that affect evaluated
            value.
          </p>

          <form.Subscribe
            selector={(state) =>
              state.values.areas[areaIndex]?.riskingEntries ?? []
            }
          >
            {(entries) => (
              <>
                {entries.length > 0 && (
                  <div className={styles.rows}>
                    {entries.map((_, entryIndex) => (
                      <RiskingEntryRow
                        key={entryIndex}
                        areaIndex={areaIndex}
                        entryIndex={entryIndex}
                        showLabels={entryIndex === 0}
                        onRemove={() => removeEntry(entryIndex)}
                      />
                    ))}
                  </div>
                )}

                {entries.length === 0 && (
                  <p className={styles.empty}>
                    No risking entries yet. Add one below.
                  </p>
                )}

                <div className={styles.footer}>
                  <Button
                    kind="tertiary"
                    size="sm"
                    renderIcon={Add}
                    onClick={addEntry}
                  >
                    Add Risking Entry
                  </Button>
                </div>
              </>
            )}
          </form.Subscribe>
        </div>
      </AccordionDetails>
    </Accordion>
  );
}

// ─────────────────────────────────────
// Single risking entry row
// ─────────────────────────────────────

interface RiskingEntryRowProps {
  areaIndex: number;
  entryIndex: number;
  showLabels: boolean;
  onRemove: () => void;
}

/**
 * A single row of inline form fields for a risking entry.
 * Labels shown only on the first row. Title/General mode switching.
 * @param {RiskingEntryRowProps} props - The row props
 * @return {JSX.Element} The entry row
 */
function RiskingEntryRow({
  areaIndex,
  entryIndex,
  showLabels,
  onRemove,
}: RiskingEntryRowProps): JSX.Element {
  const { form } = useScenarioForm();
  const prefix = `areas[${areaIndex}].riskingEntries[${entryIndex}]`;
  const enumsState = useAtomValue(enumsLoadableAtom);
  const enums = enumsState.state === 'hasData' ? enumsState.data : null;

  /**
   * Sets a deeply-nested entry field. Casts to bypass TanStack's deep path typing.
   * @param {string} field - The field name within the entry
   * @param {unknown} value - The value to set
   * @return {void}
   */
  const setField = (field: string, value: unknown) =>
    (form.setFieldValue as any)(`${prefix}.${field}`, value);

  return (
    <form.Subscribe
      selector={(state) =>
        state.values.areas[areaIndex]?.riskingEntries[entryIndex]
      }
    >
      {(entry) => {
        if (!entry) return null;
        const isTitle = entry.riskType === 'title';

        const riskPctValue =
          entry.riskPercentage !== '' ? String(entry.riskPercentage) : '';
        const pctNum = parseFloat(riskPctValue);
        const formula =
          riskPctValue && !isNaN(pctNum)
            ? `Value × ${formatNumber(
                1 - pctNum / 100,
                2,
                2,
                'decimal',
                false
              )}`
            : null;

        return (
          <div className={styles.row}>
            <div className={styles.cell}>
              <Dropdown
                id={`risk-type-${areaIndex}-${entryIndex}`}
                label="Select..."
                titleText={showLabels ? 'Risk Type' : ''}
                hideLabel={!showLabels}
                size="sm"
                items={enums?.riskTypes ?? []}
                selectedItem={
                  enums?.riskTypes.find((o) => o.value === entry.riskType) ??
                  null
                }
                itemToString={(item: EnumOption | null) => item?.label ?? ''}
                onChange={({
                  selectedItem,
                }: {
                  selectedItem: EnumOption | null;
                }) => {
                  setField('riskType', selectedItem?.value ?? '');
                  if (selectedItem?.value === 'general') {
                    setField('titleQuality', '');
                    setField('titleRiskFactor', '');
                  }
                }}
              />
            </div>

            <div className={styles.cell}>
              <Dropdown
                id={`title-quality-${areaIndex}-${entryIndex}`}
                label={isTitle ? 'Select...' : '—'}
                titleText={showLabels ? 'Title Quality' : ''}
                hideLabel={!showLabels}
                size="sm"
                disabled={!isTitle}
                items={isTitle ? (enums?.titleQualities ?? []) : []}
                selectedItem={
                  isTitle
                    ? (enums?.titleQualities.find(
                        (o) => o.value === entry.titleQuality
                      ) ?? null)
                    : null
                }
                itemToString={(item: EnumOption | null) => item?.label ?? ''}
                onChange={({
                  selectedItem,
                }: {
                  selectedItem: EnumOption | null;
                }) => {
                  setField('titleQuality', selectedItem?.value ?? '');
                  if (
                    selectedItem?.value &&
                    selectedItem.value in TITLE_QUALITY_RISK_MAP
                  ) {
                    setField(
                      'riskPercentage',
                      TITLE_QUALITY_RISK_MAP[selectedItem.value]
                    );
                  }
                }}
              />
            </div>

            <div className={styles.cell}>
              <Dropdown
                id={`title-risk-factor-${areaIndex}-${entryIndex}`}
                label={isTitle ? 'Select...' : '—'}
                titleText={showLabels ? 'Title Risk Factor' : ''}
                hideLabel={!showLabels}
                size="sm"
                disabled={!isTitle}
                items={isTitle ? (enums?.titleRiskFactors ?? []) : []}
                selectedItem={
                  isTitle
                    ? (enums?.titleRiskFactors.find(
                        (o) => o.value === entry.titleRiskFactor
                      ) ?? null)
                    : null
                }
                itemToString={(item: EnumOption | null) => item?.label ?? ''}
                onChange={({
                  selectedItem,
                }: {
                  selectedItem: EnumOption | null;
                }) => setField('titleRiskFactor', selectedItem?.value ?? '')}
              />
            </div>

            <div className={styles.cell}>
              <Dropdown
                id={`risk-interest-${areaIndex}-${entryIndex}`}
                label="Select..."
                titleText={showLabels ? 'Interest Type' : ''}
                hideLabel={!showLabels}
                size="sm"
                items={enums?.interestTypes ?? []}
                selectedItem={
                  enums?.interestTypes.find(
                    (o) => o.value === entry.interestType
                  ) ?? null
                }
                itemToString={(item: EnumOption | null) => item?.label ?? ''}
                onChange={({
                  selectedItem,
                }: {
                  selectedItem: EnumOption | null;
                }) => setField('interestType', selectedItem?.value ?? '')}
              />
            </div>

            <div className={styles.cell}>
              <TextInput
                id={`risk-pct-${areaIndex}-${entryIndex}`}
                labelText={showLabels ? 'Risk (%)' : ''}
                hideLabel={!showLabels}
                size="sm"
                type="number"
                step="0.01"
                placeholder="—"
                value={riskPctValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const n = parseFloat(e.target.value);
                  setField('riskPercentage', isNaN(n) ? '' : n);
                }}
              />
              {formula && <span className={styles.formula}>{formula}</span>}
            </div>

            <div className={styles.cellAction}>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={TrashCan}
                iconDescription="Remove"
                tooltipPosition="top"
                onClick={onRemove}
                className={styles.removeBtn}
              />
            </div>
          </div>
        );
      }}
    </form.Subscribe>
  );
}
